import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, RotateCcw } from "lucide-react";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import { getProducts, getMeta } from "@/lib/api";
import { waGeneral } from "@/lib/whatsapp";
import { useSite } from "@/context/SiteContext";

const DEFAULT_MARCAS = ["Polo Ralph Lauren", "Tommy Hilfiger", "Bespro Shop", "Abercrombie & Fitch", "Outras"];
const CATEGORIAS = ["Bonés Básicos", "Bonés Premium", "Bonés Importados", "Roupas"];
const ESTILOS = ["Trucker", "Aba Curva", "Dad Hat"];
const DISPONIBILIDADES = [
  { label: "Pronta Entrega", valor: "pronta_entrega" },
  { label: "Encomenda", valor: "encomenda" },
];

const FilterGroup = ({ title, options, active, onSelect, testid }) => (
  <div data-testid={testid}>
    <p className="mb-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-zinc-500">{title}</p>
    <div className="no-scrollbar -mx-5 flex flex-nowrap gap-2 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0">
      {options.map((opt) => {
        const isActive = active === (opt.valor ?? opt);
        return (
          <button
            key={opt.label ?? opt}
            data-testid={`${testid}-option-${(opt.label ?? opt).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            onClick={() => onSelect(isActive ? null : (opt.valor ?? opt))}
            className={`shrink-0 rounded-full border px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
              isActive
                ? "border-offwhite bg-offwhite text-obsidian"
                : "border-white/15 text-zinc-400 hover:border-silver/50 hover:text-offwhite"
            }`}
          >
            {opt.label ?? opt}
          </button>
        );
      })}
    </div>
  </div>
);

const Catalogo = () => {
  const site = useSite();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [marcas, setMarcas] = useState(DEFAULT_MARCAS);
  const [loading, setLoading] = useState(true);
  const [marca, setMarca] = useState(searchParams.get("marca") || null);
  const [categoria, setCategoria] = useState(searchParams.get("categoria") || null);
  const [estilo, setEstilo] = useState(searchParams.get("estilo") || null);
  const [disponibilidade, setDisponibilidade] = useState(searchParams.get("disponibilidade") || null);

  useEffect(() => {
    document.title = "Catálogo de Bonés | DM IMPORTS";
    getProducts()
      .then(setProducts)
      .catch(() => {})
      .finally(() => setLoading(false));
    getMeta()
      .then((m) => m.marcas?.length && setMarcas(m.marcas))
      .catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (marca && p.marca !== marca) return false;
      if (categoria && p.categoria !== categoria) return false;
      if (estilo && p.subcategoria !== estilo) return false;
      if (disponibilidade && p.disponibilidade !== disponibilidade) return false;
      return true;
    });
  }, [products, marca, categoria, estilo, disponibilidade]);

  const hasFilters = marca || categoria || estilo || disponibilidade;

  return (
    <>
      <section data-testid="catalog-hero" className="relative overflow-hidden pb-16 pt-40 sm:pb-20">
        <img src={site.images.catalogoHero} alt="Catálogo de bonés importados DM Imports" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/85 to-obsidian" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="text-[11px] font-semibold uppercase tracking-[0.35em] text-silver">
            Catálogo
          </motion.p>
          <h1 data-testid="catalog-title" className="mt-4 font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-offwhite sm:text-5xl lg:text-6xl">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>
                Bonés em
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "112%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.22, ease: [0.22, 1, 0.36, 1] }}>
                <span className="text-stroke">destaque.</span>
              </motion.span>
            </span>
          </h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-5 max-w-md text-sm text-zinc-400 sm:text-base">
            Confira alguns dos modelos disponíveis na DM Imports. Clique em "Tenho Interesse" e fale direto com a gente no WhatsApp.
          </motion.p>
        </div>
      </section>

      <section data-testid="catalog-filters" className="border-y border-white/10 bg-obsidian/90 py-5 backdrop-blur-xl lg:sticky lg:top-[72px] lg:z-40">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 sm:px-8 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex flex-col gap-5 sm:flex-row sm:flex-wrap sm:gap-10">
            <FilterGroup title="Categoria" options={CATEGORIAS} active={categoria} onSelect={setCategoria} testid="filter-categoria" />
            <FilterGroup title="Estilo" options={ESTILOS} active={estilo} onSelect={setEstilo} testid="filter-estilo" />
            <FilterGroup title="Marca" options={marcas} active={marca} onSelect={setMarca} testid="filter-marca" />
            <FilterGroup title="Disponibilidade" options={DISPONIBILIDADES} active={disponibilidade} onSelect={setDisponibilidade} testid="filter-disponibilidade" />
          </div>
          {hasFilters && (
            <button
              data-testid="filter-clear-button"
              onClick={() => { setMarca(null); setCategoria(null); setEstilo(null); setDisponibilidade(null); }}
              className="flex shrink-0 items-center gap-2 self-start rounded-full border border-white/15 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:text-offwhite lg:self-center"
            >
              <RotateCcw className="h-3.5 w-3.5" /> Limpar
            </button>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 sm:px-8 sm:py-20">
        <p data-testid="catalog-results-count" className="mb-8 text-xs uppercase tracking-[0.25em] text-zinc-500">
          {loading ? "Carregando modelos..." : `${filtered.length} ${filtered.length === 1 ? "modelo encontrado" : "modelos encontrados"}`}
        </p>

        {!loading && filtered.length === 0 ? (
          <Reveal data-testid="catalog-empty-state" className="rounded-2xl border border-white/10 bg-coal p-12 text-center">
            <p className="font-display text-xl font-bold uppercase text-offwhite">Nenhum modelo encontrado</p>
            <p className="mx-auto mt-3 max-w-sm text-sm text-zinc-400">
              Não achou o que procura? A gente encontra para você. Fale com a DM Imports no WhatsApp.
            </p>
            <a href={waGeneral()} target="_blank" rel="noopener noreferrer" data-testid="catalog-empty-whatsapp" className="mt-7 inline-flex items-center gap-2 rounded-full bg-offwhite px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.18em] text-obsidian">
              <MessageCircle className="h-4 w-4" /> Falar no WhatsApp
            </a>
          </Reveal>
        ) : (
          <div data-testid="catalog-grid" className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default Catalogo;
